import { useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Product, Category } from '../../../types';

export const UNIT_OPTIONS = ['piece', 'dozen', 'kg', 'g', 'lbs', 'oz', 'l', 'ml', 'gallon', 'tsp', 'tbsp', 'cup', 'box', 'tray', 'loaf', 'bag', 'pack', 'case'];

const emptyProductForm = {
  name: '',
  sku: '',
  category_id: '',
  description: '',
  unit: 'piece',
  price_per_unit: '',
  minimum_order_quantity: '1',
  stock_quantity: '0',
  is_available: true,
  retail_published: false,
  allergens: '',
  ingredients: '',
};

export type ProductForm = typeof emptyProductForm;

export function useCompanyProducts() {
  const { isExpired } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', category: 'all' });

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  // Product image (pending crop result)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);

  // Category modal
  const [categoryModal, setCategoryModal] = useState({
    show: false, name: '', desc: '', saving: false,
  });
  const { show: showCategoryModal, name: categoryName, desc: categoryDesc, saving: savingCategory } = categoryModal;

  // Inline category creation inside product modal
  const [inlineCategory, setInlineCategory] = useState({
    isAdding: false, name: '',
  });
  const { isAdding: isAddingNewCategory, name: newInlineCategoryName } = inlineCategory;

  // Delete confirmation
  const [deleteState, setDeleteState] = useState<{
    showModal: boolean; product: Product | null; deleting: boolean;
  }>({
    showModal: false, product: null, deleting: false,
  });
  const { showModal: showDeleteModal, product: deletingProduct, deleting } = deleteState;

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        companyAPI.getProducts(),
        companyAPI.getCategories(),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Product CRUD ──

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setProductForm(emptyProductForm);
    setPendingImageFile(null);
    setPendingImagePreview(null);
    setInlineCategory({ isAdding: false, name: '' });
    setFeedback(prev => ({ ...prev, error: '' }));
    setShowProductModal(true);
  };

  const handleOpenEdit = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      sku: product.sku,
      category_id: String(product.category.id),
      description: product.description || '',
      unit: product.unit,
      price_per_unit: String(product.price_per_unit),
      minimum_order_quantity: String(product.minimum_order_quantity),
      stock_quantity: String(product.stock_quantity),
      is_available: product.is_available,
      retail_published: (product as any).retail_published || false,
      allergens:   (product as any).allergens   || '',
      ingredients: (product as any).ingredients || '',
    });
    setPendingImageFile(null);
    setPendingImagePreview((product as any).image || null);
    setFeedback(prev => ({ ...prev, error: '' }));
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name.trim() || !productForm.sku.trim() || !productForm.price_per_unit) {
      setFeedback(prev => ({ ...prev, error: 'Name, SKU, category and price are required.' }));
      return;
    }
    if (isAddingNewCategory && !newInlineCategoryName.trim()) {
      setFeedback(prev => ({ ...prev, error: 'Please enter a name for the new category.' }));
      return;
    }
    if (!isAddingNewCategory && !productForm.category_id) {
      setFeedback(prev => ({ ...prev, error: 'Please select a category or add a new one.' }));
      return;
    }
    setSaving(true);
    setFeedback(prev => ({ ...prev, error: '' }));
    try {
      let categoryId = Number(productForm.category_id);

      if (isAddingNewCategory) {
        const catRes = await companyAPI.createCategory({ name: newInlineCategoryName.trim() });
        categoryId = catRes.data.id;
      }

      const payload = {
        name: productForm.name.trim(),
        sku: productForm.sku.trim(),
        category_id: categoryId,
        description: productForm.description.trim(),
        unit: productForm.unit,
        price_per_unit: productForm.price_per_unit,
        minimum_order_quantity: Number(productForm.minimum_order_quantity) || 1,
        stock_quantity: Number(productForm.stock_quantity) || 0,
        is_available: productForm.is_available,
        retail_published: (productForm as any).retail_published || false,
        allergens:   ((productForm as any).allergens   || '').trim(),
        ingredients: ((productForm as any).ingredients || '').trim(),
      };

      if (editingProduct) {
        await companyAPI.updateProduct(editingProduct.id, payload);
        if (pendingImageFile) {
          await companyAPI.uploadProductImage(editingProduct.id, pendingImageFile);
        }
        setFeedback(prev => ({ ...prev, success: 'Product updated successfully!' }));
      } else {
        const res = await companyAPI.createProduct(payload);
        if (pendingImageFile && res.data?.id) {
          await companyAPI.uploadProductImage(res.data.id, pendingImageFile);
        }
        setFeedback(prev => ({ ...prev, success: 'Product created successfully!' }));
      }
      setShowProductModal(false);
      fetchData();
    } catch (err: any) {
      const data = err.response?.data;
      if (data) {
        const msg = typeof data === 'string' ? data
          : data.detail || data.error
          || Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ');
        setFeedback(prev => ({ ...prev, error: msg }));
      } else {
        setFeedback(prev => ({ ...prev, error: 'Failed to save product.' }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = (product: Product) => {
    setDeleteState({ showModal: true, product, deleting: false });
  };

  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;
    setDeleteState(prev => ({ ...prev, deleting: true }));
    try {
      await companyAPI.deleteProduct(deletingProduct.id);
      setFeedback(prev => ({ ...prev, success: 'Product deleted.' }));
      setDeleteState({ showModal: false, product: null, deleting: false });
      fetchData();
    } catch {
      setFeedback(prev => ({ ...prev, error: 'Failed to delete product.' }));
    } finally {
      setDeleteState(prev => ({ ...prev, deleting: false }));
    }
  };

  // ── Category CRUD ──

  const handleCreateCategory = async () => {
    if (!categoryName.trim()) return;
    setCategoryModal(prev => ({ ...prev, saving: true }));
    try {
      await companyAPI.createCategory({ name: categoryName.trim(), description: categoryDesc.trim() });
      setFeedback(prev => ({ ...prev, success: 'Category created!' }));
      setCategoryModal({ show: false, name: '', desc: '', saving: false });
      fetchData();
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Failed to create category.';
      setFeedback(prev => ({ ...prev, error: typeof msg === 'string' ? msg : JSON.stringify(msg) }));
    } finally {
      setCategoryModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await companyAPI.deleteCategory(id);
      setFeedback(prev => ({ ...prev, success: 'Category deleted.' }));
      fetchData();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to delete category.';
      setFeedback(prev => ({ ...prev, error: msg }));
    }
  };

  // ── Filtering ──

  const filtered = products.filter((p) => {
    const matchSearch = !filters.search || p.name.toLowerCase().includes(filters.search.toLowerCase()) || p.sku.toLowerCase().includes(filters.search.toLowerCase());
    const matchCat = filters.category === 'all' || String(p.category.id) === filters.category;
    return matchSearch && matchCat;
  });

  const closeProductModal = () => setShowProductModal(false);
  const closeCategoryModal = () => setCategoryModal(prev => ({ ...prev, show: false }));
  const closeDeleteModal = () => setDeleteState(prev => ({ ...prev, showModal: false }));
  const clearSuccess = () => setFeedback(prev => ({ ...prev, success: '' }));
  const clearError = () => setFeedback(prev => ({ ...prev, error: '' }));
  const cancelInlineCategory = () => setInlineCategory({ isAdding: false, name: '' });

  const handleCategorySelectChange = (value: string) => {
    if (value === '__new__') {
      setInlineCategory(prev => ({ ...prev, isAdding: true }));
      setProductForm(prev => ({ ...prev, category_id: '' }));
    } else {
      setProductForm(prev => ({ ...prev, category_id: value }));
    }
  };

  return {
    // Auth
    isExpired,

    // State
    products,
    categories,
    loading,
    filters,
    setFilters,

    // Product modal
    showProductModal,
    editingProduct,
    productForm,
    setProductForm,
    saving,
    feedback,

    // Product image
    pendingImageFile,
    pendingImagePreview,
    setPendingImageFile,
    setPendingImagePreview,

    // Category modal
    showCategoryModal,
    categoryName,
    categoryDesc,
    savingCategory,
    setCategoryModal,

    // Inline category
    isAddingNewCategory,
    newInlineCategoryName,
    setInlineCategory,

    // Delete state
    showDeleteModal,
    deletingProduct,
    deleting,

    // Filtered data
    filtered,

    // Handlers
    handleOpenCreate,
    handleOpenEdit,
    handleSaveProduct,
    handleConfirmDelete,
    handleDeleteProduct,
    handleCreateCategory,
    handleDeleteCategory,
    closeProductModal,
    closeCategoryModal,
    closeDeleteModal,
    clearSuccess,
    clearError,
    cancelInlineCategory,
    handleCategorySelectChange,
  };
}
